import { difference } from 'lodash';
import dayjs from 'dayjs';
import { messages } from 'messages';
import { DATE_FORMAT, DATE_TIME_FORMAT, WEEKDAY_FORMAT } from './constants';
import { FilterPopoverOptionItem } from 'components/common/filter/Filter';
import { TreeDataNode } from 'antd';

export function capitalizeFirstLetter(text: string = '') {
  if (!text) {
    return text;
  }

  text = text.toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function removeSpecialCharacters(keyword: string): string {
  if (!keyword) {
    return '';
  }

  return keyword
    .toLowerCase()
    .replace('đ', 'd')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, ' ')
    .replace(/\\s+/g, ' ')
    .trim();
}

export function formatNumber(number: number, locale?: string): string {
  return new Intl.NumberFormat(locale ?? 'en-US').format(number);
}

export const stringToHslColor = (str?: string, s: number = 80, l: number = 80, o: number = 1) => {
  if (!str) return '#000000';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let h = hash % 360;
  return `hsl(${h}, ${s}%, ${l}%, ${o})`;
};

export const disabledDateUnderYear =
  (year = 18) =>
  (current: dayjs.Dayjs | null) => {
    const dayYearsAgo = dayjs().subtract(year, 'year');
    return !!current && current.isAfter(dayYearsAgo);
  };

export const formatWeekDate = (date: dayjs.Dayjs): string => {
  const today = dayjs();

  if (today.isSame(date, 'day')) {
    return messages.today;
  }

  return date.format(WEEKDAY_FORMAT);
};

export const removeEmptyChildren = (data: any[]) => {
  return data.map((itm) => {
    if (itm.children && itm.children.length === 0) {
      delete itm.children;
    } else if (itm.children) {
      itm.children = removeEmptyChildren(itm.children);
    }
    return itm;
  });
};

export const checkPositionCursor = (e: React.KeyboardEvent<HTMLDivElement>, target: HTMLDivElement) => {
  const range = window.getSelection()?.getRangeAt(0);
  const preRange = document.createRange();
  preRange.selectNodeContents(target);
  if (range) {
    preRange.setEnd(range.startContainer, range.startOffset);
    const text = preRange.cloneContents();
    const atStart = text?.textContent?.length === 0;
    const postRange = document.createRange();
    postRange.selectNodeContents(target);
    postRange.setStart(range.endContainer, range.endOffset);
    const nextText = postRange.cloneContents();
    const atEnd = nextText?.textContent?.length === 0;
    return { atEnd, atStart };
  }
  return {
    atStart: false,
    atEnd: false
  };
};

/** Thêm văn bản vào vị trí con trỏ hiện tại mà không giữ bất kỳ định dạng nào bằng cách sử dụng Range API */
export const execCommand = (text: string) => {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return;

  const range = selection.getRangeAt(0);
  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);

  range.setStartAfter(textNode);
  selection.removeAllRanges();
  selection.addRange(range);
};

export const moveCaretToEnd = (element: HTMLElement) => {
  // Tạo một đối tượng range và selection
  const range = document.createRange();
  const selection = window.getSelection();

  // Đặt range đến cuối nội dung
  range.selectNodeContents(element);
  range.collapse(false); // Di chuyển con trỏ đến cuối

  // Xóa mọi range hiện tại và đặt range mới
  selection?.removeAllRanges();
  selection?.addRange(range);

  // Đảm bảo focus vào ô contenteditable
  element.focus();
};

const TIME_SPAN = {
  day_5: 7200,
  day_1: 1440,
  hour: 60,
  minute: 0
};

export const convertTimeAgo = (date: Date | string, showTime: boolean = true, defaultTime?: string): string => {
  const minute = dayjs().diff(date, 'minute');
  if (minute > TIME_SPAN.day_5) {
    return showTime ? dayjs(date).format(DATE_TIME_FORMAT) : dayjs(date).format(DATE_FORMAT);
  }
  if (minute > TIME_SPAN.day_1) {
    return dayjs().diff(date, 'day') + ' ngày trước';
  }
  if (minute > TIME_SPAN.hour) {
    return dayjs().diff(date, 'hour') + ' giờ trước';
  }
  if (minute > TIME_SPAN.minute) {
    return minute + ' phút trước';
  }
  return defaultTime ?? 'Vừa xong';
};

export const convertTimeLeft = (
  date: Date | string | undefined,
  showTime: boolean = true,
  defaultMessage: string = 'Đã hết hạn'
): string => {
  if (!date) return defaultMessage;

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) return defaultMessage;

  const now = dayjs();
  const diffMinute = parsedDate.diff(now, 'minute');

  if (diffMinute <= 0) return defaultMessage;

  const diffHour = parsedDate.diff(now, 'hour', true);
  const diffDay = parsedDate.diff(now, 'day');

  if (diffMinute > TIME_SPAN.day_5) {
    return `${parsedDate.format(showTime ? DATE_TIME_FORMAT : DATE_FORMAT)}`;
  } else if (diffMinute > TIME_SPAN.day_1) {
    return `${diffDay} ngày nữa`;
  } else if (diffMinute > TIME_SPAN.hour) {
    const roundedHour = diffHour >= 23.5 ? Math.round(diffHour) : Math.floor(diffHour);
    return `${roundedHour} giờ nữa`;
  } else {
    return `${diffMinute} phút nữa`;
  }
};

export function isValidJson(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch (err) {
    return false;
  }
}

export function isDecimalGreaterThanHalf(num: number) {
  const result = num / 3;
  const decimalPart = result - Math.floor(result);
  return decimalPart > 0.5;
}

export function getSelectedTextInDiv(div: HTMLDivElement): string | null {
  const selection = window.getSelection();

  if (!selection || !div) {
    return null;
  }

  // Kiểm tra nếu phần văn bản được chọn nằm trong div
  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    if (div.contains(range.commonAncestorContainer)) {
      return selection.toString(); // Trả về văn bản đã chọn
    }
  }

  return null;
}

export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const extractUrls = (content: string): string[] => {
  const urlRegex = /https?:\/\/[^\s/$.?#].[^\s]*/g;
  return content.match(urlRegex) || [];
};

export const transformUrlsToLinks = (content: string) => {
  const urlPattern = /(https?:\/\/[^\s]+)/g;

  return content.replace(
    urlPattern,
    (url) => `<a href="${url}" class="text-colorPrimary underline" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
};

export const normalizeImageUrl = (url?: string, domain?: string): string => {
  if (!url) return '';
  // Chỉ thêm https nếu URL không bắt đầu bằng http hoặc https
  if (!/^https?:\/\//i.test(url)) {
    return `${domain}${url}`;
  }
  return url;
};

export function getAppName(data: Record<string, string>) {
  const url = data['al:ios:app_name'] || data['al:android:app_name'] || data['og:url'];
  const strippedUrl = url.replace(/https?:\/\//, '').replace(/^www\./, '');

  const appName = strippedUrl.split('/')[0];

  return appName;
}

export const numberToExcelCol = (number: number) => {
  const ordA = 'a'.charCodeAt(0);
  const ordZ = 'z'.charCodeAt(0);
  const len = ordZ - ordA + 1;

  let s = '';
  while (number >= 0) {
    s = String.fromCharCode((number % len) + ordA) + s;
    number = Math.floor(number / len) - 1;
  }
  return s.toUpperCase();
};

export function hasJSONStringValue(jsonString: string | null | undefined): boolean {
  return jsonString !== undefined && jsonString !== null && jsonString.trim() !== '';
}

export function isArrayOfNumbers(data: unknown): data is number[] {
  return Array.isArray(data) && data.every((item) => typeof item === 'number');
}

export function replaceStringAt(str: string, insert: string, start: number, end: number) {
  return str.slice(0, start) + insert + str.slice(end);
}

export function convertBytes(sizeInBytes: number) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let index = 0;

  while (sizeInBytes >= 1000 && index < units.length - 1) {
    sizeInBytes /= 1000;
    index++;
  }

  return `${sizeInBytes.toFixed(0)} ${units[index]}`;
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function convertStringToMatrix(dataString: string): (string | number)[][] {
  const parsedData: any[][] = JSON.parse(dataString);

  return (
    parsedData?.map((row) =>
      row.map((item) => {
        if (item === null || item === undefined) {
          return '';
        }
        return typeof item === 'number' ? item : String(item);
      })
    ) || []
  );
}

export function isStringOrNumberMatrix(data: any): data is (string | number)[][] {
  if (!Array.isArray(data)) return false;
  return data.every((row) => Array.isArray(row));
}

type KeyOfArray<T> = T extends Array<infer U> ? keyof U : never;

export function mergeArrays<T extends object, K extends KeyOfArray<T[]>>(
  array1: T[],
  array2: T[],
  key: K // Chỉ cho phép các key hợp lệ từ các phần tử trong array1/array2
): T[] {
  const map = new Map(array1.map((item) => [item[key], item]));
  // Ghi đè hoặc thêm mới từ array2
  array2.forEach((item) => {
    map.set(item[key], item);
  });

  // Trả về mảng từ Map với thứ tự giữ nguyên của array1
  return array1
    .map((item) => map.get(item[key])!) // Giữ thứ tự từ array1
    .concat(
      array2.filter((item) => !array1.some((i) => i[key] === item[key])) // Thêm mới
    );
}

export function mergeArrays2<T extends object, K extends KeyOfArray<T[]>>(
  array1: T[],
  array2: T[],
  key1: K,
  key2: K
): T[] {
  // Tạo key duy nhất bằng cách kết hợp hai giá trị key
  const createCompositeKey = (item: T): string => {
    return `${item[key1] as unknown as string | number}___${item[key2] as unknown as string | number}`;
  };

  // Map từ composite key đến object
  const map = new Map<string, T>(array1.map((item) => [createCompositeKey(item), item]));

  // Ghi đè hoặc thêm mới từ array2
  array2.forEach((item) => {
    map.set(createCompositeKey(item), item);
  });

  // Lấy các item từ array1 (có thể đã bị ghi đè bởi array2), giữ nguyên thứ tự array1
  const fromArray1 = array1.map((item) => {
    const key = createCompositeKey(item);
    return map.get(key)!; // chắc chắn tồn tại vì ít nhất có từ array1
  });

  // Các item mới từ array2 không có trong array1 (dựa trên cả 2 key)
  const newFromArray2 = array2.filter((item2) => {
    const key2 = createCompositeKey(item2);
    return !array1.some((item1) => createCompositeKey(item1) === key2);
  });

  return [...fromArray1, ...newFromArray2];
}

export function roundToNearestMultipleX(numb: number, x: number) {
  return Math.ceil(numb / x) * x;
}

export function filterOptions<T extends object, K extends KeyOfArray<T[]>>(options: T[], keyword: string, key: K) {
  if (!keyword) return options;
  const normalizedKeyword = removeVietnameseAccents(keyword.toLowerCase());
  return options.filter((option) => {
    const normalizedLabel = removeVietnameseAccents((option[key] as string).toLowerCase());
    return normalizedLabel.includes(normalizedKeyword);
  });
}

export function removeVietnameseAccents(keyword: string | number = ''): string {
  return String(keyword)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

export const convertToTreeNode = (items: FilterPopoverOptionItem[]): TreeDataNode[] => {
  return items.map((item) => ({
    title: String(item.label),
    key: String(item.value), // Đảm bảo key là string
    children: item.children ? convertToTreeNode(item.children) : undefined
  }));
};

export const calculateTimeDifference = (fromTime: string | Date, toTime: string | Date): string => {
  const startTime = dayjs(fromTime);
  const finishTime = dayjs(toTime);

  if (finishTime.isBefore(startTime)) {
    return '00:00:00';
  }

  const diffInSeconds = finishTime.diff(startTime, 'second');
  const hours = Math.floor(diffInSeconds / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  const seconds = diffInSeconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export function includesRemoveSpecial(text?: string, keyword?: string): boolean {
  if (!keyword?.trim()) return true; // Nếu không có keyword, trả về true

  if (!text) return false; // Nếu text rỗng/null, không thể chứa keyword

  const normalizedLabel = removeVietnameseAccents(text.toLowerCase().trim());
  return normalizedLabel.includes(removeVietnameseAccents(keyword.toLowerCase().trim()));
}

export const findNodeByKey = (nodes: any[], key: number): any => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
};

export const findParentKey = (nodes: any[], targetKey: number, parent: any = null): any => {
  for (const node of nodes) {
    if (node.key === targetKey) {
      return parent;
    }

    if (node.children) {
      const found = findParentKey(node.children, targetKey, node);
      if (found) return found;
    }
  }

  return null;
};

const collectChildKeys = (node: any): number[] => {
  if (!node.children || node.children.length === 0) return [node.key];

  let keys: number[] = [node.key];
  for (const child of node.children) {
    keys.push(child.key);
    keys = keys.concat(collectChildKeys(child));
  }
  return keys;
};

export const getAllChildrenByKey = (nodes: any[], parentKey: number): any => {
  const parentNode = findNodeByKey(nodes, parentKey);
  if (!parentNode) return [];

  return collectChildKeys(parentNode);
};

export const getParentChecked = (
  nodes: any[],
  childKey: number,
  selectedKeys: number[],
  parentKeys: number[] = []
): number[] => {
  const parentNode = findParentKey(nodes, childKey);
  if (!parentNode) return selectedKeys;

  const parentChildrenKeys = parentNode.children.map((child: any) => child.key);
  const differenceKey = difference(parentChildrenKeys, selectedKeys);
  if (differenceKey.length === 0) {
    const parentChecked = [...parentKeys, parentNode.key];
    return getParentChecked(nodes, parentNode.key, [...selectedKeys, parentNode.key], parentChecked);
  }
  return parentKeys;
};

export const getAllParentKeys = (nodes: any[], childKey: number): number[] => {
  const parentNode = findParentKey(nodes, childKey);
  if (!parentNode) return [];

  const parentKeys = [parentNode.key];
  return parentKeys.concat(getAllParentKeys(nodes, parentNode.key));
};

export const pickRandom = <T>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

export const pickByNumber = <T>(arr: T[], num: number): T => {
  if (arr.length === 0) throw new Error('Array is empty');
  const index = Math.abs(num) % arr.length;
  return arr[index];
};
